import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/api';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function FICCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleCallback = useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const response = await apiClient.client.post('/fic/auth/callback', { code, state });
      return response.data;
    },
    onSuccess: () => {
      setStatus('success');
      toast.success('Integrazione FIC connessa con successo!');
      setTimeout(() => {
        navigate('/settings', { replace: true });
      }, 2000);
    },
    onError: (error: any) => {
      setStatus('error');
      const message = error.response?.data?.detail || 'Errore durante la connessione a FIC';
      setErrorMessage(message);
      toast.error(message);
    },
  });

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setErrorMessage(`Errore OAuth: ${error}`);
      return;
    }

    if (code && state) {
      handleCallback.mutate({ code, state });
    } else {
      setStatus('error');
      setErrorMessage('Parametri OAuth mancanti');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connessione in corso...
            </h2>
            <p className="text-gray-600">
              Stiamo collegando il tuo account Fatture in Cloud
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connessione riuscita!
            </h2>
            <p className="text-gray-600">
              Il tuo account Fatture in Cloud è stato collegato con successo.
              Verrai reindirizzato alle impostazioni...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Errore di connessione
            </h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <button
              onClick={() => navigate('/settings', { replace: true })}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Torna alle Impostazioni
            </button>
          </>
        )}
      </div>
    </div>
  );
}
